import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';

import {IStore} from 'redux/interface';
import {getAuthorizationAction} from 'redux/auth/authAsyncActions';
import AvatarDialogue from 'components/avatar-dialogue';
import Loader from 'components/loader';

import {postToParent} from './bridge';

import './styles.scss';

// UCSF-3818: chrome-less avatar surface for the admin-web Experiments run page (loaded in an iframe).
//
// It reuses the exact surface a patient sees on the Assistant tab (no experiment mode) and self-
// authenticates via GritxSite's own Auth0 client: when the viewer already has a verbalize.ai session
// in this browser (refresh token in localStorage), the app bootstrap (getAuthorizationAction) signs
// them in silently and resolves their bot_user. No token is passed from the parent. Lifecycle events
// are posted to the parent through the bridge so the run page can refresh its History view.

function numberParam(value: string | null): number | undefined {
  if (value == null || value.trim() === '') {
    return undefined;
  }
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
}

export const AvatarEmbed: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const {auth0User, isFinishAuthorization, auth0Client} = useSelector((state: IStore) => state.auth);
  const [sessionKey, setSessionKey] = useState(0);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  const dialogueId = numberParam(searchParams.get('dialogueId'));
  const botId = numberParam(searchParams.get('botId'));
  const avatarId = numberParam(searchParams.get('avatarId'));
  const apId = numberParam(searchParams.get('apId'));
  const voiceOnly = searchParams.get('voiceOnly') === 'true';
  const title = searchParams.get('title') || 'Assistant';

  const hasContext = dialogueId != null && botId != null;
  const authReady = isFinishAuthorization;
  const authed = Boolean(auth0User);

  // Tell the parent it has no verbalize.ai session to reuse, so it can guide the viewer to sign in.
  useEffect(() => {
    if (authReady && !authed) {
      postToParent({type: 'auth-required'});
    }
  }, [authReady, authed]);

  // The surface is up and the lobby will render; the parent may reveal the iframe.
  useEffect(() => {
    if (authReady && authed && hasContext) {
      postToParent({type: 'ready'});
    }
  }, [authReady, authed, hasContext]);

  // The videocomponent's end reason, kept loosely typed so this file does not depend on the
  // package's (currently stale) type exports; the surface's onEnd prop accepts it unchanged.
  function handleEnd(reason: unknown) {
    postToParent({type: 'session-ended', reason: reason == null ? undefined : String(reason)});
    // Remounting returns the viewer to the lobby; nothing starts again until they press Start.
    setSessionKey(key => key + 1);
  }

  // Cross-site embed: the parent (admin-web) and this tenant site are different registrable domains,
  // so storage is partitioned and the tenant-site refresh token is not visible here. A popup is
  // first-party to Auth0 and reuses the viewer's existing Auth0 tenant session (established when they
  // signed into admin-web on the same Auth0 tenant), so it typically completes without re-entering
  // credentials; the token then lives in this partition, so later visits are silent.
  async function handleSignIn() {
    setSigningIn(true);
    setSignInError(null);
    try {
      await auth0Client.loginWithPopup();
      dispatch(getAuthorizationAction());
    } catch (error) {
      console.error('avatar-embed sign-in failed', error);
      setSignInError('sign-in-failed');
    } finally {
      setSigningIn(false);
    }
  }

  const body = useMemo(() => {
    if (!authReady) {
      return <Loader/>;
    }

    if (!authed) {
      return (
        <div className="avatar-embed__notice" role="status">
          <p className="avatar-embed__notice-text">
            <FormattedMessage
              id="gritx.avatarEmbed.authRequired"
              defaultMessage="Sign in to start the conversation."
            />
          </p>
          <button
            type="button"
            className="avatar-embed__btn"
            onClick={handleSignIn}
            disabled={signingIn}
          >
            {signingIn
              ? <FormattedMessage id="gritx.avatarEmbed.signingIn" defaultMessage="Signing in…"/>
              : <FormattedMessage id="gritx.avatarEmbed.signIn" defaultMessage="Sign in"/>}
          </button>
          {signInError && (
            <p className="avatar-embed__notice-error" role="alert">
              <FormattedMessage
                id="gritx.avatarEmbed.signInFailed"
                defaultMessage="Could not sign in. Please allow the popup and try again."
              />
            </p>
          )}
        </div>
      );
    }

    if (!hasContext) {
      return (
        <div className="avatar-embed__notice" role="alert">
          <p className="avatar-embed__notice-text">
            <FormattedMessage
              id="gritx.avatarEmbed.missingContext"
              defaultMessage="This panel is missing the run context (dialogue and bot)."
            />
          </p>
        </div>
      );
    }

    return (
      <AvatarDialogue
        key={sessionKey}
        dialogueId={dialogueId as number}
        botId={botId as number}
        avatarId={avatarId}
        apId={apId}
        title={title}
        voiceOnly={voiceOnly}
        onEnd={handleEnd}
      />
    );
  }, [authReady, authed, hasContext, sessionKey, dialogueId, botId, avatarId, apId, title, voiceOnly, signingIn, signInError]);

  return <main className="avatar-embed">{body}</main>;
};

export default AvatarEmbed;
